import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLang } from "../lang/useLanguage";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const hdr = () => ({ Authorization: `Bearer ${token()}` });
const money = (v) => v != null ? `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺` : "—";

const getMonthNames = () => {
  const l = getLang();
  const m = l.qualityCost || {};
  return ["", m.jan||"Oca", m.feb||"Şub", m.mar||"Mar", m.apr||"Nis", m.may||"May", m.jun||"Haz", m.jul||"Tem", m.aug||"Ağu", m.sep||"Eyl", m.oct||"Eki", m.nov||"Kas", m.dec||"Ara"];
};

const getCategoryLabels = () => {
  const l = getLang();
  const m = l.qualityCost || {};
  return {
    dimensional:    m.catDimensional    || "Boyutsal",
    visual:         m.catVisual         || "Görsel",
    functional:     m.catFunctional     || "Fonksiyonel",
    material:       m.catMaterial       || "Malzeme",
    process:        m.catProcess        || "Proses",
    documentation:  m.catDocumentation  || "Dokümantasyon",
    other:          m.catOther          || "Diğer",
  };
};

const KpiCard = ({ label, value, icon, color, sub, alert, t }) => (
  <div style={{ background: t.bg.card, border: `1px solid ${alert ? "#ef444466" : t.border.card}`, borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card, position: "relative" }}>
    {alert && <div style={{ position: "absolute", top: 8, right: 8, fontSize: 16 }}>🚨</div>}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: color || t.accent.blue }}>{value ?? "—"}</div>
        {sub && <div style={{ fontSize: 11, color: t.text.muted, marginTop: 4 }}>{sub}</div>}
      </div>
      <span style={{ fontSize: 26, opacity: 0.7 }}>{icon}</span>
    </div>
  </div>
);

export default function QualityCost() {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
  const m = l.qualityCost || {};
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState("dashboard");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/quality-cost/summary?month=${month}&year=${year}`, { headers: hdr() });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [month, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const MONTH_NAMES   = getMonthNames();
  const CATEGORY_LABELS = getCategoryLabels();

  const ncr        = data?.ncr_summary  || {};
  const scrap      = data?.scrap_summary || {};
  const grandTotal = (ncr.grand_total || 0) + (scrap.net_scrap_loss || 0);

  const trendData = (data?.trend || []).map(r => ({
    name:                          `${MONTH_NAMES[r.month]} ${r.year}`,
    [m.trendQualityCost||"Kalite Maliyeti"]: parseFloat(r.total_cost || 0).toFixed(0),
    [m.trendNcrCount   ||"NCR Sayısı"]:      r.ncr_count,
    [m.trendReturns    ||"Müşteri İadesi"]:   r.customer_returns,
  }));

  const categoryData = (data?.by_category || []).map(r => ({
    name:    CATEGORY_LABELS[r.ncr_category] || r.ncr_category,
    count:   r.count,
    maliyet: parseFloat(r.total_cost || 0).toFixed(0),
  }));

  const tabs = [
    ["dashboard", `📊 ${m.tabSummary    || "Özet"}`],
    ["recurring", `🔄 ${m.tabRecurring  || "Tekrarlayan"}`],
    ["kaizen",    `💡 ${m.tabKaizen     || "Kaizen Önerileri"}`],
    ["trend",     `📈 ${m.tabTrend      || "Trend"}`],
  ];

  const thStyle = { padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" };
  const tdBg = (i) => ({ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row });

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); @keyframes sf-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>
              💸 {m.title || "Kalite Maliyet Raporu"}
            </h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {m.subtitle || "Cost of Quality · NCR Maliyetleri · Tekrar Eden Problemler · ISO 9001"}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <select value={month} onChange={e => setMonth(parseInt(e.target.value))} style={{ padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, cursor: "pointer" }}>
              {MONTH_NAMES.slice(1).map((mn, i) => <option key={i+1} value={i+1}>{mn}</option>)}
            </select>
            <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, cursor: "pointer" }}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄</button>
          </div>
        </div>

        {/* KPI */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 24 }}>
          <KpiCard label={m.kpiTotalCost      || "Toplam Kalite Maliyeti"} value={money(grandTotal)}              icon="💸" color="#ef4444" alert={grandTotal > 50000} t={t} />
          <KpiCard label={m.kpiInternalError  || "İç Hata Maliyeti"}       value={money(ncr.total_internal)}      icon="🏭" color="#f97316" sub={m.kpiInternalSub || "İşçilik + Makine"} t={t} />
          <KpiCard label={m.kpiExternalError  || "Dış Hata Maliyeti"}      value={money(ncr.total_external)}      icon="🚚" color="#ef4444" sub={m.kpiExternalSub || "Nakliye + Ceza"} t={t} />
          <KpiCard label={m.kpiScrapLoss      || "Hurda Net Kaybı"}         value={money(scrap.net_scrap_loss)}    icon="🗑️" color="#ef4444" sub={`${scrap.scrap_count || 0} ${m.records || "kayıt"}`} t={t} />
          <KpiCard label={m.kpiCustomerReturn || "Müşteri İadesi"}          value={ncr.customer_returns || 0}      icon="📦" color="#f97316" sub={m.thisMonth || "Bu ay"} alert={(ncr.customer_returns || 0) > 3} t={t} />
        </div>

        {/* Sekmeler */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 12, padding: 4, width: "fit-content" }}>
          {tabs.map(([v, label]) => (
            <button key={v} onClick={() => setMainTab(v)} style={{ padding: "9px 16px", borderRadius: 9, border: "none", cursor: "pointer", background: mainTab === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none", color: mainTab === v ? t.text.primary : t.text.muted, fontSize: 13, fontWeight: 600, boxShadow: mainTab === v ? t.shadow.card : "none", transition: "all 0.2s" }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: "#ef4444", borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{m.loading || "Yükleniyor..."}</span>
          </div>
        ) : (
          <>
            {/* ÖZET */}
            {mainTab === "dashboard" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>💸 {m.costBreakdown || "Maliyet Dağılımı"}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {[
                        { label: m.internalError || "İç Hata (İşçilik, Makine)", value: ncr.total_internal  || 0, color: "#f97316", icon: "🏭" },
                        { label: m.rework        || "Yeniden İşleme",            value: ncr.total_rework   || 0, color: "#a78bfa", icon: "🔧" },
                        { label: m.externalError || "Dış Hata (Nakliye, Ceza)", value: ncr.total_external  || 0, color: "#ef4444", icon: "🚚" },
                        { label: m.otherImpact   || "Diğer Kalite Etkisi",      value: ncr.total_impact   || 0, color: "#eab308", icon: "📋" },
                        { label: m.scrapNetLoss  || "Hurda Net Kaybı",          value: scrap.net_scrap_loss || 0, color: "#6b7280", icon: "🗑️" },
                      ].map(item => (
                        <div key={item.label}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: t.text.secondary }}>{item.icon} {item.label}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: item.color }}>{money(item.value)}</span>
                          </div>
                          <div style={{ height: 6, background: t.bg.tag, borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ width: `${grandTotal > 0 ? Math.min((item.value / grandTotal) * 100, 100) : 0}%`, height: "100%", background: item.color, borderRadius: 3 }} />
                          </div>
                        </div>
                      ))}
                      <div style={{ borderTop: `2px solid ${t.border.default}`, paddingTop: 10, display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{m.total || "TOPLAM"}</span>
                        <span style={{ fontSize: 15, fontWeight: 800, color: "#ef4444" }}>{money(grandTotal)}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>📊 {m.errorCategory || "Hata Kategorisi"}</div>
                    {categoryData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={categoryData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                          <XAxis dataKey="name" stroke={t.chart.axis} tick={{ fontSize: 9, fill: t.chart.axis }} />
                          <YAxis yAxisId="left"  stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                          <YAxis yAxisId="right" orientation="right" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                          <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                          <Legend />
                          <Bar yAxisId="left"  dataKey="count"   fill="#ef4444"   radius={[3,3,0,0]} name={m.ncrCount || "NCR Sayısı"} />
                          <Bar yAxisId="right" dataKey="maliyet" fill="#f9731666" radius={[3,3,0,0]} name={m.costTl    || "Maliyet (₺)"} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>{m.noNcrThisMonth || "Bu ay NCR yok"}</div>}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
                  {[
                    { label: m.ncrCount      || "NCR Sayısı",      value: ncr.ncr_count || 0,                           icon: "🔴", color: "#ef4444" },
                    { label: m.criticalNcr   || "Kritik NCR",      value: ncr.critical_count || 0,                     icon: "🚨", color: "#ef4444" },
                    { label: m.scrapWeight   || "Hurda Ağırlığı",  value: `${(scrap.total_weight || 0).toFixed(1)} kg`, icon: "⚖️", color: "#f97316" },
                    { label: m.recovery      || "Geri Kazanım",    value: money(scrap.total_recovery),                  icon: "💚", color: "#22c55e" },
                  ].map(card => (
                    <div key={card.label} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card, textAlign: "center" }}>
                      <div style={{ fontSize: 24, marginBottom: 6 }}>{card.icon}</div>
                      <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{card.label}</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: card.color }}>{card.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TEKRARLAYAN PROBLEMLER */}
            {mainTab === "recurring" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {(data?.recurring_problems || []).length === 0 ? (
                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 48, textAlign: "center", boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                    <div style={{ color: t.text.placeholder, fontSize: 14 }}>{m.noRecurring || "Bu ay tekrarlayan problem yok! Harika."}</div>
                  </div>
                ) : (
                  <div style={{ background: t.bg.card, border: `1px solid #ef444433`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                    <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: "#ef444411" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#ef4444" }}>🔄 {m.recurringTitle || "Tekrarlayan Problemler — Acil Kaizen Gerektirir!"}</div>
                      <span style={{ fontSize: 12, color: t.text.muted }}>{data.recurring_problems.length} {m.product || "ürün"}</span>
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr style={{ background: t.bg.theadRow }}>
                        {[m.productCode||"Ürün Kodu", m.ncrCount||"NCR Sayısı", m.totalCost||"Toplam Maliyet", m.severities||"Şiddetler", m.riskLevel||"Risk Seviyesi"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {data.recurring_problems.map((r, i) => {
                          const risk      = r.ncr_count >= 5 ? (m.riskCritical||"KRİTİK") : r.ncr_count >= 3 ? (m.riskHigh||"YÜKSEK") : (m.riskMedium||"ORTA");
                          const riskColor = r.ncr_count >= 5 ? "#ef4444"                   : r.ncr_count >= 3 ? "#f97316"               : "#eab308";
                          return (
                            <tr key={i} style={tdBg(i)}>
                              <td style={{ padding: "12px 12px", fontSize: 14, fontWeight: 800, color: t.text.primary }}>{r.product_code}</td>
                              <td style={{ padding: "12px 12px" }}>
                                <span style={{ fontSize: 16, fontWeight: 800, color: "#ef4444" }}>{r.ncr_count}</span>
                                <span style={{ fontSize: 11, color: t.text.muted, marginLeft: 4 }}>{m.times||"kez"}</span>
                              </td>
                              <td style={{ padding: "12px 12px", fontSize: 13, fontWeight: 700, color: "#ef4444" }}>{money(r.total_cost)}</td>
                              <td style={{ padding: "12px 12px", fontSize: 11, color: t.text.secondary }}>{r.severities}</td>
                              <td style={{ padding: "12px 12px" }}>
                                <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 20, color: riskColor, background: `${riskColor}18`, border: `1px solid ${riskColor}44` }}>{risk}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* KAİZEN ÖNERİLERİ */}
            {mainTab === "kaizen" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ padding: "14px 20px", background: "#a78bfa11", border: "1px solid #a78bfa33", borderRadius: 12, fontSize: 13, color: "#a78bfa" }}>
                  💡 {m.kaizenInfo || "Son 90 günde 2+ NCR alan ürünler için otomatik Kaizen önerisi oluşturuldu. Bu problemler tekrar etmeden önce kök neden analizi yapılmalı."}
                </div>
                {(data?.kaizen_suggestions || []).length === 0 ? (
                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 48, textAlign: "center", boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                    <div style={{ color: t.text.placeholder, fontSize: 14 }}>{m.noKaizen || "Son 90 günde tekrarlayan problem yok."}</div>
                  </div>
                ) : (data.kaizen_suggestions.map((r, i) => {
                  const urgency      = r.ncr_count >= 4 ? (m.urgencyVeryUrgent||"Çok Acil") : r.ncr_count >= 3 ? (m.urgencyUrgent||"Acil") : (m.urgencySuggested||"Önerilen");
                  const urgencyColor = r.ncr_count >= 4 ? "#ef4444" : r.ncr_count >= 3 ? "#f97316" : "#a78bfa";
                  return (
                    <div key={i} style={{ background: t.bg.card, border: `1px solid ${urgencyColor}33`, borderRadius: 14, padding: 20, boxShadow: t.shadow.card }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                            <span style={{ fontSize: 15, fontWeight: 800, color: t.text.primary }}>{r.product_code}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, color: urgencyColor, background: `${urgencyColor}18`, border: `1px solid ${urgencyColor}44` }}>{urgency}</span>
                          </div>
                          <div style={{ fontSize: 13, color: t.text.secondary, marginBottom: 6 }}>
                            {m.last90Days || "Son 90 günde"} <strong style={{ color: "#ef4444" }}>{r.ncr_count} NCR</strong> {m.recorded || "kaydı"} · {m.totalCost_ || "Toplam maliyet"}: <strong style={{ color: "#ef4444" }}>{money(r.total_cost)}</strong>
                          </div>
                          <div style={{ fontSize: 12, color: t.text.muted }}>
                            💡 {m.kaizenAction || "Önerilen Aksiyon: Kök neden analizi yapın → CAPA oluşturun → Proses kontrol planını güncelleyin"}
                          </div>
                        </div>
                        <div style={{ fontSize: 32, opacity: 0.5 }}>💡</div>
                      </div>
                    </div>
                  );
                }))}
              </div>
            )}

            {/* TREND */}
            {mainTab === "trend" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>📈 {m.trend12months || "12 Aylık Kalite Maliyet Trendi"}</div>
                  {trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                        <XAxis dataKey="name" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                        <YAxis yAxisId="left"  stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                        <YAxis yAxisId="right" orientation="right" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                        <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                        <Legend />
                        <Line yAxisId="left"  type="monotone" dataKey={m.trendQualityCost||"Kalite Maliyeti"} stroke="#ef4444" strokeWidth={2.5} dot={{ fill: "#ef4444", r: 4 }} />
                        <Line yAxisId="right" type="monotone" dataKey={m.trendNcrCount   ||"NCR Sayısı"}      stroke="#f97316" strokeWidth={2}   dot={{ fill: "#f97316", r: 3 }} />
                        <Line yAxisId="right" type="monotone" dataKey={m.trendReturns    ||"Müşteri İadesi"}   stroke="#a78bfa" strokeWidth={2}   dot={{ fill: "#a78bfa", r: 3 }} strokeDasharray="5 5" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>{m.noTrendData || "Trend verisi yok"}</div>}
                </div>

                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, fontSize: 13, fontWeight: 700, color: t.text.primary }}>📋 {m.monthlyDetail || "Aylık Detay"}</div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: t.bg.theadRow }}>
                      {[m.month_||"Ay", m.ncrCount||"NCR Sayısı", m.customerReturn||"Müşteri İadesi", m.qualityCost_||"Kalite Maliyeti", m.statusLabel||"Durum"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {(data?.trend || []).length === 0 ? (
                        <tr><td colSpan={5} style={{ padding: 32, textAlign: "center", color: t.text.placeholder }}>{m.noData || "Veri yok"}</td></tr>
                      ) : (data?.trend || []).map((r, i) => {
                        const cost        = parseFloat(r.total_cost || 0);
                        const status      = cost > 50000 ? (m.statusCritical||"Kritik") : cost > 20000 ? (m.statusHigh||"Yüksek") : (m.statusNormal||"Normal");
                        const statusColor = cost > 50000 ? "#ef4444"                    : cost > 20000 ? "#f97316"                 : "#22c55e";
                        return (
                          <tr key={i} style={tdBg(i)}>
                            <td style={{ padding: "11px 12px", fontSize: 13, fontWeight: 700, color: t.text.primary }}>{MONTH_NAMES[r.month]} {r.year}</td>
                            <td style={{ padding: "11px 12px", fontSize: 13, color: r.ncr_count > 10 ? "#ef4444" : t.text.secondary, fontWeight: r.ncr_count > 10 ? 700 : 400 }}>{r.ncr_count}</td>
                            <td style={{ padding: "11px 12px", fontSize: 13, color: r.customer_returns > 3 ? "#f97316" : t.text.secondary, fontWeight: r.customer_returns > 3 ? 700 : 400 }}>{r.customer_returns}</td>
                            <td style={{ padding: "11px 12px", fontSize: 13, fontWeight: 700, color: "#ef4444" }}>{money(cost)}</td>
                            <td style={{ padding: "11px 12px" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, color: statusColor, background: `${statusColor}18`, border: `1px solid ${statusColor}44` }}>{status}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
